import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from "@mui/material";
import { FormEvent, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { useConfig } from "../../utils/useConfig";
import { useData } from "../../utils/useData";
import { PlanSelection } from "../shared/PlanSelection";
import { Employee } from "../../models";

type AssignToPlanProps = {
  open: boolean;
  handleClose: () => void;
  selectedEmployees: Employee[];
};

const DEFAULT_VALUE = "-1";

export const AssignToPlan = ({
  open,
  handleClose,
  selectedEmployees
}: AssignToPlanProps) => {
  const { config } = useConfig();
  const { employerId, employees } = useData();
  const { getToken } = useKindeAuth();

  const [selectedPlans, setSelectedPlans] = useState<
    { planId: string; tierId: string }[]
  >([]);
  const [error, setError] = useState("");

  const selectedEmployeesWithoutPlan = selectedEmployees.filter(
    (employee) => employee.accounts.length === 0
  );

  const mutation = useMutation({
    mutationFn: async () => {
      await Promise.all(
        selectedEmployeesWithoutPlan.map(async (employee) => {
          const response = await fetch(
            `${config?.API_URL}/employers/${employerId}/employees/${employee.id}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${await getToken()}`,
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                ...employee,
                plans: selectedPlans.filter(
                  (selectedPlan) => selectedPlan.tierId !== DEFAULT_VALUE
                )
              })
            }
          );

          if (!response.ok)
            throw new Error("There was a problem assign these plans");
        })
      );
    },
    onSuccess: () => {
      toast.success("Plans assigned successfully");
      employees.refetch();
      handleClose();
    },
    onError: (error: Error) => {
      console.error(error.message);
      setError(error.message);
    }
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth={true}>
      <Box component={"form"} onSubmit={handleSubmit}>
        <Box display="flex" justifyContent="space-between">
          <DialogTitle>
            Assign {selectedEmployeesWithoutPlan.length} user
            {selectedEmployeesWithoutPlan.length > 1 ? "s" : ""} to plan(s)
          </DialogTitle>
        </Box>
        <DialogContent>
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            <Grid item>
              <Typography>General instructions on assigning plans</Typography>
              <Typography color="gray">
                Only users without a plan can have their plans set here. If you
                want to add a new plan to a user with an active plan please edit
                that user directly.
              </Typography>
            </Grid>
            <Grid item container>
              <PlanSelection
                selectedPlans={selectedPlans}
                setSelectedPlans={setSelectedPlans}
                DEFAULT_VALUE={DEFAULT_VALUE}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={mutation.isLoading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
